import {
  AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild,
} from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Simple, beginner-friendly "Light Mixer" embedded inside a blog post. It shows that
// every color on a screen is three lights added together: Red, Green and Blue. The
// reader types a color and the widget draws three waves, one per light, each its own
// colour. The HEIGHT (amplitude) of each wave is that channel's amount, so brightness
// reads as a taller or shorter wave, never as fading. The post markdown marks a mount
// point with <!--LIGHT-START-->...<!--LIGHT-END--> around a static fallback (kept for
// crawlers and no-JS readers); BlogPostPageComponent swaps this live component in.
@Component( {
  selector: 'app-light-mixer',
  templateUrl: './light-mixer.component.html',
  styleUrls: [ './light-mixer.component.scss' ],
} )
export class LightMixerComponent implements AfterViewInit, OnDestroy {

  // Heading shown OUTSIDE the glass panel (matches the color-converter pattern).
  @Input() heading = 'See Any Color As Three Lights';

  @ViewChild( 'waves', { static: true } ) wavesRef!: ElementRef<HTMLCanvasElement>;

  // A curated set of starting colours, one chosen at random per mount so the widget
  // opens on a different named colour each visit instead of always the brand blue.
  // Every entry keeps all three channels clearly present at DIFFERENT amounts, so the
  // opacity-as-brightness idea (three lights of unequal height) reads at a glance, and
  // they span the hue wheel (blue, coral, teal, amber, violet, rose, lime, magenta...).
  private readonly starters = [
    '#549eff', '#ff7a59', '#2ec4b6', '#ffb13b', '#9b6dff',
    '#ff6fa3', '#8bd450', '#4fd6e8', '#e061c9', '#ffd23f',
  ];

  // Base color the reader picks; seeded with a random starter (see above), unless the
  // post passes a color via [hex] (then it opens on that). Its red, green and blue
  // channels are all present at different amounts, so the opacity-as-brightness idea
  // shows at a glance.
  baseHex = this.pickStarter();
  text = this.baseHex;

  // Optional starting color from the post ([hex]="post.accent"): the widget opens on it
  // instead of a random starter. Ignored when no valid hex is bound.
  @Input() set hex( value: string ) {
    const rgb = this.parseHex( value );
    if ( rgb ) {
      this.baseHex = '#' + String( value ).replace( /^#/, '' ).toLowerCase();
      this.text = this.baseHex;
      this.recompute();
    }
  }

  // Channel amounts 0..255 and a rough name, bound by the template.
  r = 0;
  g = 0;
  b = 0;
  name = '';

  // Approximate visible wavelength of each primary light, in nanometres. Red is the
  // longest wave, blue the shortest, matching the on-screen wave frequencies. Shown in the
  // legend so the reader reads each channel as a real light (wavelength + power) rather
  // than a bare 0..255 number.
  readonly wavelengths = { r: 700, g: 550, b: 450 };

  // Power of a light: how strongly it shines, as a percentage of full strength (its
  // 0..255 amount). 255 -> 100%, 128 -> 50%, 0 -> 0%.
  pct( value: number ): number {
    return Math.round( ( value / 255 ) * 100 );
  }

  // Each light: a PURE primary colour (so the additive blend gives exact results, red
  // plus green is yellow, all three is white) and a spatial frequency. Red is the
  // longest wave (fewest cycles), blue the shortest (most cycles), mirroring real light.
  private readonly lights = [
    { key: 'r', color: 'rgb(255,0,0)', cycles: 2.6 },
    { key: 'g', color: 'rgb(0,255,0)', cycles: 3.3 },
    { key: 'b', color: 'rgb(0,0,255)', cycles: 4.1 },
  ];

  private phase = 0;
  private rafId = 0;
  private running = false;
  private readonly speed = 0.05;
  // Offscreen buffer used to render one light at a time at full strength before it is
  // composited onto the visible canvas (see drawWaves).
  private layer?: HTMLCanvasElement;

  // One engagement event on first interaction, not per keystroke.
  private tracked = false;

  constructor( private zone: NgZone, private analyticsService: AnalyticsService ) {
  }

  private trackUse(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'light-mixer' } );
  }

  // Pick a random starting colour from the curated set (one per mount).
  private pickStarter(): string {
    return this.starters[ Math.floor( Math.random() * this.starters.length ) ];
  }

  ngAfterViewInit(): void {
    this.recompute();
    this.zone.runOutsideAngular( () => {
      this.kickInitialPaint( 0 );
      this.running = true;
      const loop = () => {
        if ( !this.running ) {
          return;
        }
        this.phase += this.speed;
        this.drawWaves();
        this.rafId = requestAnimationFrame( loop );
      };
      this.rafId = requestAnimationFrame( loop );
    } );
  }

  ngOnDestroy(): void {
    this.running = false;
    if ( this.rafId ) {
      cancelAnimationFrame( this.rafId );
    }
  }

  // Retry until the canvas has a width, then paint one frame (so it is never left
  // blank if the rendering clock is throttled while the post body is mounting).
  private kickInitialPaint( attempt: number ): void {
    if ( this.wavesRef.nativeElement.clientWidth > 0 ) {
      this.drawWaves();
      return;
    }
    if ( attempt > 40 ) {
      return;
    }
    setTimeout( () => this.kickInitialPaint( attempt + 1 ), 50 );
  }

  // ---- inputs -----------------------------------------------------------------

  onText( value: string ): void {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.text = '#' + raw;
    if ( this.parseHex( this.text ) ) {
      this.baseHex = this.text;
      this.recompute();
    }
    this.trackUse();
  }

  onPicker( value: string ): void {
    this.text = ( value || '' ).toLowerCase();
    this.baseHex = this.text;
    this.recompute();
    this.trackUse();
  }

  onKey( event: KeyboardEvent ): void {
    if ( event.ctrlKey || event.metaKey ) {
      return;
    }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  // ---- helpers ----------------------------------------------------------------

  private parseHex( text: string ): [ number, number, number ] | null {
    // Only full 6-digit hex is accepted (no 3-digit shorthand), matching the other widgets.
    const h = ( text || '' ).trim().replace( /^#/, '' );
    if ( !/^[0-9a-fA-F]{6}$/.test( h ) ) {
      return null;
    }
    return [
      parseInt( h.slice( 0, 2 ), 16 ),
      parseInt( h.slice( 2, 4 ), 16 ),
      parseInt( h.slice( 4, 6 ), 16 ),
    ];
  }

  private recompute(): void {
    const rgb = this.parseHex( this.baseHex ) || [ 0, 0, 0 ];
    this.r = rgb[ 0 ];
    this.g = rgb[ 1 ];
    this.b = rgb[ 2 ];
    this.name = this.nameOf( rgb );
  }

  private nameOf( c: [ number, number, number ] ): string {
    const [ r, g, b ] = c.map( v => v / 255 ) as [ number, number, number ];
    const mx = Math.max( r, g, b ), mn = Math.min( r, g, b );
    if ( mx < 0.04 ) {
      return 'black';
    }
    const sat = ( mx - mn ) / mx;
    if ( sat < 0.12 ) {
      return mx > 0.85 ? 'white' : 'grey';
    }
    let h: number;
    const d = mx - mn;
    if ( mx === r ) {
      h = ( ( g - b ) / d ) % 6;
    } else if ( mx === g ) {
      h = ( b - r ) / d + 2;
    } else {
      h = ( r - g ) / d + 4;
    }
    h = ( h * 60 + 360 ) % 360;
    const names: [ number, string ][] = [
      [ 15, 'red' ], [ 45, 'orange' ], [ 70, 'yellow' ], [ 160, 'green' ],
      [ 200, 'cyan' ], [ 255, 'blue' ], [ 290, 'violet' ], [ 330, 'magenta' ], [ 360, 'red' ],
    ];
    for ( const [ edge, nm ] of names ) {
      if ( h <= edge ) {
        return nm;
      }
    }
    return 'color';
  }

  // ---- drawing ----------------------------------------------------------------

  // Size the canvas buffer to whole device pixels and map CSS pixels onto it. Rounding
  // to an integer buffer matters: phones report a FRACTIONAL devicePixelRatio (2.625,
  // 2.75...), and an unrounded buffer would never equal the integer canvas.width, so
  // the canvas would reallocate every frame and tear. Returns null until laid out.
  private fit( cv: HTMLCanvasElement ): CanvasRenderingContext2D | null {
    const cssW = cv.clientWidth, cssH = cv.clientHeight;
    if ( cssW < 1 || cssH < 1 ) {
      return null;
    }
    const dpr = Math.min( window.devicePixelRatio || 1, 3 );
    const w = Math.max( 1, Math.round( cssW * dpr ) );
    const h = Math.max( 1, Math.round( cssH * dpr ) );
    if ( cv.width !== w || cv.height !== h ) {
      cv.width = w; cv.height = h;
    }
    const ctx = cv.getContext( '2d' )!;
    ctx.setTransform( w / cssW, 0, 0, h / cssH, 0, 0 );
    return ctx;
  }

  private drawWaves(): void {
    const cv = this.wavesRef.nativeElement;
    const ctx = this.fit( cv );
    if ( !ctx ) {
      return;
    }
    const W = cv.clientWidth, H = cv.clientHeight, cy = H / 2;

    // Polished deep-slate backdrop: a soft vertical gradient rather than pure black, so
    // the panel reads as an intentional dark surface that still lets additive light show.
    ctx.globalCompositeOperation = 'source-over';
    const bg = ctx.createLinearGradient( 0, 0, 0, H );
    bg.addColorStop( 0, '#121826' );
    bg.addColorStop( 1, '#080b12' );
    ctx.fillStyle = bg;
    ctx.fillRect( 0, 0, W, H );

    // One shared baseline for all three lights.
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo( 0, cy ); ctx.lineTo( W, cy ); ctx.stroke();

    // Additive light: all three waves are the SAME full height, so they always overlap.
    // The channel amount sets each light's OPACITY (255 fully opaque, 128 half, 0 not
    // drawn). To keep brightness UNIFORM along each line, every light is first drawn at
    // full strength onto an offscreen layer with NORMAL compositing, so its line, glow and
    // the bright crests where the wave doubles back at its peaks do not additively pile up
    // and over-brighten. The whole layer is then added onto the canvas with 'lighter' at
    // that channel's alpha, so the glow and those glowing crests scale with the value
    // exactly like the line, while additive mixing between DIFFERENT lights still happens
    // on the canvas (red plus green is yellow at the centre, all three trend to white).
    const amp = H / 2 - 16;
    const sx = cv.width / W, sy = cv.height / H;
    const layer = this.ensureLayer( cv );
    const lctx = layer.getContext( '2d' )!;
    lctx.lineCap = 'round';
    lctx.lineJoin = 'round';
    const values: { [ key: string ]: number } = { r: this.r, g: this.g, b: this.b };
    for ( const light of this.lights ) {
      const alpha = values[ light.key ] / 255;
      if ( alpha <= 0.002 ) {
        continue;
      }
      // Draw this light at full strength on the transparent layer (centre-referenced
      // phase, so the three waves coincide in the middle of the canvas).
      lctx.setTransform( 1, 0, 0, 1, 0, 0 );
      lctx.clearRect( 0, 0, layer.width, layer.height );
      lctx.setTransform( sx, 0, 0, sy, 0, 0 );
      lctx.globalCompositeOperation = 'source-over';
      lctx.globalAlpha = 1;
      lctx.strokeStyle = light.color;
      lctx.shadowColor = light.color;
      lctx.shadowBlur = 16;
      lctx.lineWidth = 7.5;
      lctx.beginPath();
      for ( let x = 0; x <= W; x += 2 ) {
        const u = ( x - W / 2 ) / W;
        const y = cy - amp * Math.sin( u * light.cycles * 2 * Math.PI - this.phase );
        if ( x === 0 ) {
          lctx.moveTo( x, y );
        } else {
          lctx.lineTo( x, y );
        }
      }
      lctx.stroke();
      // Add the whole layer onto the canvas, scaled by this channel's opacity.
      ctx.save();
      ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = alpha;
      ctx.drawImage( layer, 0, 0 );
      ctx.restore();
    }
  }

  // Lazily create / resize the offscreen layer to match the visible canvas buffer.
  private ensureLayer( cv: HTMLCanvasElement ): HTMLCanvasElement {
    if ( !this.layer ) {
      this.layer = document.createElement( 'canvas' );
    }
    if ( this.layer.width !== cv.width || this.layer.height !== cv.height ) {
      this.layer.width = cv.width;
      this.layer.height = cv.height;
    }
    return this.layer;
  }
}
